import { useCallback, useState } from 'react';
import {
  createObjectExport,
  getObjectExportAsset,
  getObjectExportStatus,
} from '@/features/exports';
import type { PerformanceBreakdownRow, ProjectPerformance } from '@/entities/media-campaign';
import { i18n } from '@/shared/i18n';
import { track } from '@/shared/analytics';

export type ReportFormat = 'pptx' | 'xlsx';

/** `pptx` renders the client-facing deck; `xlsx` renders the same figures as a raw annex. */
const TEMPLATE_KEY_BY_FORMAT: Record<ReportFormat, string> = {
  pptx: 'performance-report',
  xlsx: 'performance-data',
};

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

export interface ReportSections {
  /** Only meaningful when `performance.dimension === 'platform'` - see `buildPayload`. */
  platforms: boolean;
  /** Only meaningful when `performance.dimension === 'campaign'` - see `buildPayload`. */
  campaigns: boolean;
}

export interface GenerateReportInput {
  format: ReportFormat;
  /**
   * The response the dashboard already rendered. `generate` serialises this object as-is
   * and never fetches its own copy - a second fetch could return different numbers (a
   * campaign delivering in real time between the two requests) and the report would
   * silently disagree with what the marketer was looking at when they clicked Generate.
   */
  performance: ProjectPerformance;
  projectName: string;
  window: { startDate: string; endDate: string };
  scopeLabel?: string;
  sections?: Partial<ReportSections>;
}

export type ReportExportStatus = 'idle' | 'generating' | 'completed' | 'failed';

export interface UseReportExportReturn {
  generate: (input: GenerateReportInput) => Promise<void>;
  status: ReportExportStatus;
  downloadUrl: string | null;
  isGenerating: boolean;
  error: Error | null;
}

/**
 * Mirrors the wire shape `performanceReportSchema` validates
 * (`apps/api/src/agents/artifacts/export/templates/performance-report.export.ts`).
 * Hand-mirrored rather than imported: that module's dependency chain pulls in NestJS,
 * `pptxgenjs`, and an apps/api-local `src/...` path alias that agent-studio's own
 * tsconfig cannot resolve, so importing it would fail `type-check` here rather than
 * catch a real mismatch. Keeping this shape in sync with the backend schema is manual
 * and unchecked by the compiler across the app boundary - a diff between the two is
 * exactly what a shared contract test elsewhere should catch.
 */
interface PerformanceReportTotals {
  spend: number | null;
  currency: string | null;
  currencyCount: number;
  isSingleCurrency: boolean;
  unknownCurrencySpend: number;
  impressions: number;
  clicks: number;
  conversions: number;
}

interface PerformanceReportPlatformRow {
  platform: string;
  spend: number;
  currency: string | null;
  impressions: number;
  clicks?: number;
  conversions?: number;
  spendReported: boolean;
}

interface PerformanceReportCampaignRow {
  campaignName: string;
  platform?: string;
  spend: number;
  currency: string | null;
  impressions: number;
  clicks?: number;
  conversions?: number;
  spendReported: boolean;
}

interface PerformanceReportPayload {
  projectName: string;
  window: { startDate: string; endDate: string };
  scopeLabel?: string;
  totals: PerformanceReportTotals;
  platforms: PerformanceReportPlatformRow[];
  campaigns?: PerformanceReportCampaignRow[];
}

/**
 * The one scalar spend figure the report may print for `totals.spend`, or `null` when
 * there isn't one.
 *
 * `ProjectPerformance` has no such scalar by design - it carries `spendByCurrency: []`
 * instead, because converting between currencies was ruled out (no FX rates on hand, and
 * inventing one would fabricate a figure). A number is only unambiguous when exactly one
 * known currency reports spend and nothing does so in an unspecified currency; every other
 * shape - nothing reporting at all, more than one known currency, or any unknown-currency
 * spend - sends `null` rather than a placeholder. The backend's `resolveTotalsSpend`
 * (`performance-report.export.ts`) picks which of "Not reported" / "Multiple currencies" /
 * "Currency unspecified" applies from `currencyCount` and `unknownCurrencySpend` alone, so
 * `null` here is honest in every one of those cases rather than standing in for a number
 * this feature cannot state.
 */
function deriveTotalsSpend(
  performance: ProjectPerformance,
): Pick<PerformanceReportTotals, 'spend' | 'currency'> {
  if (performance.spendByCurrency.length !== 1 || performance.unknownCurrencySpend !== 0) {
    return { spend: null, currency: null };
  }
  const [only] = performance.spendByCurrency;
  return { spend: only.spend, currency: only.currency };
}

function buildTotals(performance: ProjectPerformance): PerformanceReportTotals {
  return {
    ...deriveTotalsSpend(performance),
    // Distinct KNOWN currencies only - `unknownCurrencySpend` carries the unknown bucket
    // separately, and folding it in here would double-count it in the backend's decision.
    currencyCount: performance.spendByCurrency.length,
    isSingleCurrency: performance.isSingleCurrency,
    unknownCurrencySpend: performance.unknownCurrencySpend,
    impressions: performance.impressions,
    clicks: performance.clicks,
    conversions: performance.conversions,
  };
}

function mapPlatformRow(row: PerformanceBreakdownRow): PerformanceReportPlatformRow {
  return {
    platform: row.label,
    spend: row.spend,
    currency: row.currency,
    impressions: row.impressions,
    clicks: row.clicks,
    conversions: row.conversions,
    spendReported: row.spendReported,
  };
}

function mapCampaignRow(row: PerformanceBreakdownRow): PerformanceReportCampaignRow {
  return {
    campaignName: row.label,
    spend: row.spend,
    currency: row.currency,
    impressions: row.impressions,
    clicks: row.clicks,
    conversions: row.conversions,
    spendReported: row.spendReported,
  };
}

/**
 * `performance.breakdown` carries whichever one dimension the dashboard currently shows -
 * there is no second, unrequested breakdown sitting in memory to fall back on. So a
 * platform report only gets `platforms` rows when the loaded breakdown is already
 * dimensioned by platform (campaigns rows likewise), rather than reinterpreting one
 * dimension's rows as the other or fetching the missing one - which would violate the same
 * "serialise what's on screen" rule as the totals above.
 */
function buildPayload(input: GenerateReportInput): PerformanceReportPayload {
  const { performance } = input;
  const includePlatforms = input.sections?.platforms ?? true;
  const includeCampaigns = input.sections?.campaigns ?? true;

  const platforms =
    includePlatforms && performance.dimension === 'platform'
      ? performance.breakdown.map(mapPlatformRow)
      : [];
  const campaigns =
    includeCampaigns && performance.dimension === 'campaign'
      ? performance.breakdown.map(mapCampaignRow)
      : undefined;

  return {
    projectName: input.projectName,
    window: input.window,
    scopeLabel: input.scopeLabel,
    totals: buildTotals(performance),
    platforms,
    campaigns,
  };
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Mirrors `AnalyticsEvent`'s `performance_report_failed` error_code union in
 * shared/analytics/events.ts - hand-mirrored, not imported, the same trade as
 * `PerformanceReportPayload` above: events.ts keeps its prop aliases un-exported so a
 * wire-contract change there doesn't ripple through this feature's public API.
 */
export type ReportFailureCode = 'export_service_failed' | 'export_timed_out' | 'unknown';

/**
 * Thrown when the export service itself reports the job as failed. The message resolves in
 * the constructor rather than from a module constant, so it follows the current language.
 */
class ReportGenerationFailedError extends Error {
  constructor() {
    super(i18n.t('projects:performanceReports.useReportExport.reportGenerationFailed'));
  }
}

/** Thrown when the job never leaves "processing" within `MAX_POLL_DURATION_MS`. */
class ReportGenerationTimedOutError extends Error {
  constructor() {
    super(i18n.t('projects:performanceReports.useReportExport.reportGenerationTimedOut'));
  }
}

/**
 * Classifies by error identity, never by message: the export client (`@/features/exports`)
 * and the underlying HTTP layer can throw with server-provided text, and a project or
 * client name could be in there. Anything that isn't one of this hook's own sentinel
 * errors - a network failure, an unexpected response shape, anything - falls to `unknown`
 * rather than risk that text reaching analytics.
 */
function reportFailureCode(error: unknown): ReportFailureCode {
  if (error instanceof ReportGenerationFailedError) return 'export_service_failed';
  if (error instanceof ReportGenerationTimedOutError) return 'export_timed_out';
  return 'unknown';
}

/** A count of the sections the request actually asked for, not a measurement. */
function sectionCount(input: GenerateReportInput): number {
  const includePlatforms = input.sections?.platforms ?? true;
  const includeCampaigns = input.sections?.campaigns ?? true;
  return [includePlatforms, includeCampaigns].filter(Boolean).length;
}

/**
 * Drives the Reports view's generate-and-poll flow through the shared export client
 * (`@/features/exports`) rather than a second HTTP path for the same backend endpoints.
 */
export function useReportExport(projectId: string): UseReportExportReturn {
  const [status, setStatus] = useState<ReportExportStatus>('idle');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const generate = useCallback(
    async (input: GenerateReportInput): Promise<void> => {
      setStatus('generating');
      setError(null);
      setDownloadUrl(null);

      const templateKey = TEMPLATE_KEY_BY_FORMAT[input.format];

      try {
        const created = await createObjectExport({
          templateKey,
          data: buildPayload(input) as unknown as Record<string, unknown>,
        });

        let currentStatus = created.status;
        const startTime = Date.now();
        // Stops as soon as the export leaves "processing" - on failure this exits the loop
        // immediately rather than continuing to poll a job that has already given its answer.
        while (currentStatus === 'processing' && Date.now() - startTime < MAX_POLL_DURATION_MS) {
          await wait(POLL_INTERVAL_MS);
          const polled = await getObjectExportStatus({
            controllerId: created.controllerId,
            templateKey,
          });
          currentStatus = polled.status;
        }

        if (currentStatus === 'failed') {
          throw new ReportGenerationFailedError();
        }
        if (currentStatus !== 'completed') {
          throw new ReportGenerationTimedOutError();
        }

        const asset = await getObjectExportAsset({
          controllerId: created.controllerId,
          templateKey,
        });

        setDownloadUrl(asset.url);
        setStatus('completed');
        track('performance_report_generated', {
          project_id: projectId,
          format: input.format,
          section_count: sectionCount(input),
        });
      } catch (err) {
        const nextError =
          err instanceof Error
            ? err
            : new Error(
                i18n.t('projects:performanceReports.useReportExport.failedToGenerateReport'),
              );
        setError(nextError);
        setStatus('failed');
        track('performance_report_failed', {
          project_id: projectId,
          format: input.format,
          error_code: reportFailureCode(err),
        });
      }
    },
    [projectId],
  );

  return {
    generate,
    status,
    downloadUrl,
    isGenerating: status === 'generating',
    error,
  };
}
